// @tennis-matches/services/settings
// Migrated from src/services/settings/settings.remote.js

import { getSupabaseClient, getSupabaseUser } from "../supabase/index.js";
import { DEFAULT_SETTINGS } from "./defaults.js";

/**
 * Generates a device/session identifier for Phase 1 (no user_id yet)
 * Uses localStorage to persist the identifier
 */
function __getIdentifier() {
  const key = "tm_settings_identifier";
  let id = null;
  try {
    id = localStorage.getItem(key);
  } catch (_) {}
  if (!id) {
    // Generate a simple identifier (can be replaced with user_id later)
    id = `device_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    try {
      localStorage.setItem(key, id);
    } catch (_) {}
  }
  return String(id || "").trim();
}

async function __getCurrentUserId() {
  try {
    const u = await getSupabaseUser();
    const id = String(u?.id || "").trim();
    return id || null;
  } catch (_) {
    return null;
  }
}

function __readLocalSettings() {
  try {
    const raw = localStorage.getItem("tm_user_settings");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
    return null;
  } catch (_) {
    return null;
  }
}

function __writeLocalSettings(settings) {
  try {
    localStorage.setItem("tm_user_settings", JSON.stringify(settings));
  } catch (_) {}
}

const USER_SETTINGS_CACHE_MS = 15000;
let __userSettingsInflight = null;
let __userSettingsInflightKey = "";
let __userSettingsCache = null;

function __pickPreferredHomeKey(settingsObj) {
  return String(
    settingsObj?.preferredHomeApp || settingsObj?.preferred_home_app || "",
  )
    .trim()
    .toLowerCase();
}

const __VALID_PREFERRED_HOME_KEYS = new Set([
  "trainerstunden",
  "schleifchen",
  "matchmixer",
  "clubmeisterschaft",
  "medenspiele",
  "tennis-live",
  "minigames",
]);

function __mergePreferredHomeFromLocal(dbSettings) {
  const merged =
    dbSettings && typeof dbSettings === "object" ? { ...dbSettings } : {};
  const local = __readLocalSettings();
  if (!local || typeof local !== "object") return merged;

  const localPref = __pickPreferredHomeKey(local);
  const dbPref = __pickPreferredHomeKey(merged);
  if (
    localPref &&
    localPref !== "hub" &&
    __VALID_PREFERRED_HOME_KEYS.has(localPref) &&
    (!dbPref || dbPref === "hub")
  ) {
    merged.preferredHomeApp = localPref;
    merged.preferred_home_app = localPref;
  }
  return merged;
}

async function __loadUserSettingsImpl() {
  const supabase = await getSupabaseClient();
  const authUserId = supabase ? await __getCurrentUserId() : null;

  // Ohne Auth-Session kein DB-Roundtrip (RLS/is_global_superadmin → 401/42501 auf /login).
  if (!authUserId) {
    const local = __readLocalSettings();
    if (local) {
      return { ok: true, settings: local, reason: "local-anonymous" };
    }
    return {
      ok: true,
      settings: DEFAULT_SETTINGS,
      reason: "defaults-anonymous",
    };
  }

  // 1) DB versuchen (eingeloggt)
  if (supabase) {
    try {
      const identifier = __getIdentifier();
      let data = null;
      let lastError = null;

      if (authUserId) {
        const { data: d, error: e } = await supabase
          .from("user_settings")
          .select("settings")
          .eq("user_id", authUserId)
          .maybeSingle();
        data = d;
        lastError = e;
      }

      if (!data && !lastError) {
        const { data: d, error: e } = await supabase
          .from("user_settings")
          .select("settings")
          .eq("identifier", identifier)
          .maybeSingle();
        data = d;
        lastError = e;
      }

      if (data?.settings && typeof data.settings === "object") {
        const merged = __mergePreferredHomeFromLocal(data.settings);
        __writeLocalSettings(merged);
        return { ok: true, settings: merged };
      }

      if (lastError) {
        const code = String(lastError?.code || "");
        if (code !== "42P01" && code !== "42703" && code !== "42501") {
          console.warn("[settings] DB load failed, falling back:", lastError);
        }
      }
    } catch (e) {
      console.warn("[settings] DB load error, falling back:", e);
    }
  }

  // 2) localStorage als Fallback
  const local = __readLocalSettings();
  if (local) {
    return { ok: true, settings: local, reason: "local-fallback" };
  }

  // 3) Defaults
  return { ok: true, settings: DEFAULT_SETTINGS };
}

/**
 * Loads user settings.
 * Priorität:
 *   1) DB (user-gebundener Datensatz, dann identifier-gebundener)
 *   2) localStorage als Fallback (deckt Fälle ab, in denen die DB-Persistenz
 *      scheitert – z. B. weil die Migration noch nicht ausgerollt ist)
 *   3) DEFAULT_SETTINGS
 *
 * @returns {Promise<{ok: boolean, settings?: object, reason?: string}>}
 */
export async function loadUserSettings(options = {}) {
  const force = Boolean(options?.force);
  if (!force && __userSettingsInflight) {
    return __userSettingsInflight;
  }

  // Inflight sofort setzen (vor await getUser), sonst starten parallele Caller
  // zwei identische user_settings-Requests.
  const run = (async () => {
    const cacheKey = (await __getCurrentUserId()) || __getIdentifier();
    const now = Date.now();
    if (
      !force &&
      __userSettingsCache &&
      __userSettingsCache.key === cacheKey &&
      now - __userSettingsCache.ts < USER_SETTINGS_CACHE_MS
    ) {
      return __userSettingsCache.result;
    }

    __userSettingsInflightKey = cacheKey;
    const result = await __loadUserSettingsImpl();
    __userSettingsCache = { key: cacheKey, ts: Date.now(), result };
    return result;
  })();

  const promise = run.finally(() => {
    if (__userSettingsInflight === promise) {
      __userSettingsInflight = null;
      __userSettingsInflightKey = "";
    }
  });
  __userSettingsInflight = promise;
  return promise;
}

function invalidateUserSettingsCache() {
  __userSettingsCache = null;
  __userSettingsInflight = null;
  __userSettingsInflightKey = "";
}

/**
 * Saves user settings.
 *
 * Reihenfolge:
 *  1) localStorage (immer)
 *  2) DB: bevorzugt user-gebundener Datensatz (`user_id`).
 *     Wenn die Spalte `user_id` noch nicht existiert (alte Migration), fallback
 *     auf `identifier`-Datensatz – damit ist Phase-1-Kompatibilität gewahrt.
 *
 * @param {object} settings
 * @returns {Promise<{ok: boolean, persisted?: boolean, reason?: string}>}
 */
export async function saveUserSettings(settings) {
  if (!settings || typeof settings !== "object") {
    return { ok: false, reason: "invalid-settings" };
  }

  // 1) localStorage – immer, als Cache und Offline-Fallback
  try {
    __writeLocalSettings(settings);
  } catch (_) {
    return { ok: false, reason: "localStorage-failed" };
  }

  const supabase = await getSupabaseClient();
  if (!supabase) {
    return { ok: true, persisted: false, reason: "no-supabase" };
  }

  const updated_at = new Date().toISOString();
  const identifier = __getIdentifier();
  const userId = await __getCurrentUserId();

  // 2a) Bevorzugt user_id (cross-device)
  if (userId) {
    try {
      const { data: existing, error: readError } = await supabase
        .from("user_settings")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();

      if (readError) {
        const code = String(readError?.code || "");
        if (code !== "42P01" && code !== "42703") {
          return {
            ok: true,
            persisted: false,
            reason: readError.message || "db-error",
          };
        }
      } else if (existing?.id) {
        const { error } = await supabase
          .from("user_settings")
          .update({ settings, updated_at })
          .eq("id", existing.id);

        if (!error) {
          invalidateUserSettingsCache();
          return { ok: true, persisted: true };
        }

        const code = String(error?.code || "");
        if (code !== "42P01" && code !== "42703") {
          return {
            ok: true,
            persisted: false,
            reason: error.message || "db-error",
          };
        }
      } else {
        const { error } = await supabase
          .from("user_settings")
          .insert({ user_id: userId, settings, updated_at });

        if (!error) {
          invalidateUserSettingsCache();
          return { ok: true, persisted: true };
        }

        const code = String(error?.code || "");
        if (code !== "42P01" && code !== "42703") {
          return {
            ok: true,
            persisted: false,
            reason: error.message || "db-error",
          };
        }
      }
      // 42P01 / 42703 → Tabelle bzw. user_id-Spalte fehlt: fallback auf identifier
    } catch (e) {
      return {
        ok: true,
        persisted: false,
        reason: String(e?.message || "db-error"),
      };
    }
  }

  // 2b) Fallback: identifier-basiert (anon oder altes Schema ohne user_id)
  try {
    const { error } = await supabase
      .from("user_settings")
      .upsert(
        { identifier, settings, updated_at },
        { onConflict: "identifier" },
      );

    if (!error) {
      invalidateUserSettingsCache();
      return {
        ok: true,
        persisted: true,
        reason: userId ? "legacy-identifier" : undefined,
      };
    }

    const code = String(error?.code || "");
    if (code === "42P01") {
      return { ok: true, persisted: false, reason: "table-missing" };
    }
    return { ok: true, persisted: false, reason: error.message || "db-error" };
  } catch (e) {
    return {
      ok: true,
      persisted: false,
      reason: String(e?.message || "db-error"),
    };
  }
}
